import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  headerLines,
  parseHeaderLines,
  verifyDraft,
  type TAuthPresetSample,
  type TAuthPresetSpec,
  type TVerifyResult,
} from '../../entities/auth-preset';

export interface ISampleDraft {
  headersText: string;
  body: string;
  httpMethod: string;
  path: string;
  query: string;
}

export const EMPTY_SAMPLE: ISampleDraft = {
  headersText: '',
  body: '',
  httpMethod: 'POST',
  path: '',
  query: '',
};

export const sampleFromStored = (sample: TAuthPresetSample): ISampleDraft => ({
  headersText: headerLines(sample.headers),
  body: sample.body ?? '',
  httpMethod: sample.httpMethod,
  path: sample.path ?? '',
  query: sample.query ?? '',
});

export const samplePayload = (sample: ISampleDraft) => ({
  headers: parseHeaderLines(sample.headersText),
  ...(sample.body ? { body: sample.body } : {}),
  httpMethod: sample.httpMethod || 'POST',
  ...(sample.path ? { path: sample.path } : {}),
  ...(sample.query ? { query: sample.query } : {}),
});

export const sampleIsUsable = (sample: ISampleDraft): boolean =>
  Object.keys(parseHeaderLines(sample.headersText)).length > 0;

export const notAskableYet = (spec: TAuthPresetSpec | null): string | null => {
  if (!spec) return 'Nothing to check yet.';
  if (!spec.name.trim()) return 'Give the scheme a name and this starts checking.';
  if (!/^[a-z0-9-]+$/.test(spec.key)) {
    return 'The key is lowercase letters, digits and dashes — "acme-internal".';
  }

  const { header, parse } = spec.signature.location;
  if (!header.trim()) return 'Name the header the signature arrives in and this starts checking.';
  if (parse.kind === 'kv' && !parse.signatureKey.trim()) {
    return 'Say which key the signature sits under and this starts checking.';
  }

  const time = spec.timestamp.location;
  if (time.kind === 'header' && !time.header.trim()) {
    return 'Name the header the time arrives in, or move the time elsewhere.';
  }
  if (time.kind === 'same_header' && parse.kind !== 'kv') {
    return 'A header read whole has no keys in it, so the time cannot be one of them.';
  }
  return null;
};

const useDebounced = <T>(value: T, delay = 400): T => {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
};

export interface IDraftVerify {
  result: TVerifyResult | undefined;
  error: unknown;
  isPending: boolean;
  isStale: boolean;
  blocked: string | null;
}

// An empty `secret` is left off the body on purpose: `secretChecked: false` is not a signature mismatch.
export const useDraftVerify = (
  spec: TAuthPresetSpec | null,
  sample: ISampleDraft,
  secret: string,
): IDraftVerify => {
  const live = JSON.stringify({ spec, sample, secret });
  const settled = useDebounced(live);
  const blocked = notAskableYet(spec);

  // Readiness must be judged on the settled payload, never the live one, or a keystroke fires the previous draft.
  const payload = useMemo(
    () =>
      JSON.parse(settled) as { spec: TAuthPresetSpec | null; sample: ISampleDraft; secret: string },
    [settled],
  );
  const ready =
    payload.spec !== null && notAskableYet(payload.spec) === null && sampleIsUsable(payload.sample);

  const query = useQuery({
    queryKey: ['auth-preset-verify', settled],
    enabled: ready,
    retry: false,
    queryFn: () => {
      if (!payload.spec) throw new Error('Nothing to check yet.');
      return verifyDraft({
        spec: payload.spec,
        ...samplePayload(payload.sample),
        ...(payload.secret ? { secret: payload.secret } : {}),
      });
    },
  });

  return {
    result: query.data,
    error: query.error,
    isPending: ready && query.isFetching,
    isStale: ready && settled !== live,
    blocked,
  };
};
